import { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Check,
  X,
  ArrowLeftRight,
  Loader2,
  Truck,
  DollarSign,
  Clock,
  CheckCircle2,
  ArrowRight,
  Trash2,
  RotateCcw,
} from "lucide-react";
import { Link } from "react-router-dom";
import { getDismissed, dismissOffer, restoreOffer } from "@/lib/offerDismissals";

interface OfferWithNegotiation {
  id: string;
  load_id: string;
  rate_amount: number;
  status: string;
  created_at: string;
  notes: string | null;
  load: {
    load_number: string;
    origin_city: string;
    origin_state: string;
    destination_city: string;
    destination_state: string;
    pickup_date: string;
    equipment_type: string;
    total_miles: number | null;
  } | null;
  negotiation: {
    id: string;
    counter_rate_amount: number;
    status: string;
    created_at: string;
    notes: string | null;
  } | null;
}



async function fetchCarrierOffersWithNegotiations(
  userId: string
): Promise<OfferWithNegotiation[]> {
  // Fetch offers
  const { data: offers, error } = await supabase
    .from("offers")
    .select(
      `id, load_id, rate_amount, status, created_at, notes,
       loads (load_number, origin_city, origin_state, destination_city, destination_state, pickup_date, equipment_type, total_miles)`
    )
    .eq("created_by", userId)
    .in("status", [
      "pending",
      "accepted_by_carrier",
      "countered",
      "accepted",
      "rejected",
    ])
    .order("created_at", { ascending: false })
    .limit(50);

  if (error) throw error;

  // For countered offers, fetch the latest negotiation
  const counteredOfferIds = (offers || [])
    .filter((o: any) => o.status === "countered")
    .map((o: any) => o.id);

  let negotiationsMap: Record<string, any> = {};
  if (counteredOfferIds.length > 0) {
    const { data: negotiations } = await supabase
      .from("negotiations")
      .select("id, offer_id, counter_rate_amount, status, created_at, notes")
      .in("offer_id", counteredOfferIds)
      .order("created_at", { ascending: false });

    if (negotiations) {
      // Keep only the latest negotiation per offer
      for (const neg of negotiations) {
        if (!negotiationsMap[neg.offer_id]) {
          negotiationsMap[neg.offer_id] = neg;
        }
      }
    }
  }

  return (offers || []).map((o: any) => ({
    id: o.id,
    load_id: o.load_id,
    rate_amount: o.rate_amount,
    status: o.status,
    created_at: o.created_at,
    notes: o.notes ?? null,
    load: o.loads
      ? {
          load_number: o.loads.load_number,
          origin_city: o.loads.origin_city,
          origin_state: o.loads.origin_state,
          destination_city: o.loads.destination_city,
          destination_state: o.loads.destination_state,
          pickup_date: o.loads.pickup_date,
          equipment_type: o.loads.equipment_type,
          total_miles: o.loads.total_miles,
        }
      : null,
    negotiation: negotiationsMap[o.id] || null,
  }));
}

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, { label: string; variant: "default" | "secondary" | "destructive" | "outline" }> = {
    pending: { label: "Pending", variant: "secondary" },
    accepted_by_carrier: { label: "Awaiting Broker", variant: "default" },
    countered: { label: "Counter Received", variant: "outline" },
    accepted: { label: "Accepted", variant: "default" },
    rejected: { label: "Declined", variant: "destructive" },
  };
  const info = map[status] || { label: status, variant: "secondary" as const };
  return <Badge variant={info.variant}>{info.label}</Badge>;
}

export function CarrierOffersSection() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [counterAmounts, setCounterAmounts] = useState<Record<string, string>>({});
  const [showCounter, setShowCounter] = useState<Record<string, boolean>>({});
  // Per-offer terminal lock — once the carrier acts on a row, hide its action
  // controls immediately so a fast second click cannot re-fire the RPC.
  const [lockedOfferIds, setLockedOfferIds] = useState<Set<string>>(new Set());
  const [dismissed, setDismissed] = useState<Set<string>>(() => getDismissed(user?.id || ''));
  const [showCleared, setShowCleared] = useState(false);

  useEffect(() => {
    setDismissed(getDismissed(user?.id || ''));
  }, [user?.id]);

  const handleClear = (offerId: string) => {
    if (!user?.id) return;
    setDismissed(dismissOffer(user.id, offerId));
  };
  const handleRestore = (offerId: string) => {
    if (!user?.id) return;
    setDismissed(restoreOffer(user.id, offerId));
  };

  const { data: offers = [], isLoading } = useQuery({
    queryKey: ["carrier-offers-full", user?.id],
    queryFn: () => fetchCarrierOffersWithNegotiations(user!.id),
    enabled: !!user,
    staleTime: 15_000,
    refetchInterval: 30_000,
  });

  const invalidateAll = () => {
    queryClient.invalidateQueries({ queryKey: ["carrier-offers-full", user?.id] });
    queryClient.invalidateQueries({ queryKey: ["carrier-offers", user?.id] });
  };

  const lockOffer = (offerId: string) =>
    setLockedOfferIds((prev) => { const n = new Set(prev); n.add(offerId); return n; });
  const unlockOffer = (offerId: string) =>
    setLockedOfferIds((prev) => { const n = new Set(prev); n.delete(offerId); return n; });

  const handleAction = async (
    offerId: string,
    negotiationId: string,
    action: "accept" | "reject" | "counter",
    counterAmount?: number
  ) => {
    if (lockedOfferIds.has(offerId)) return;
    lockOffer(offerId);
    setActionLoading(negotiationId + action);
    try {
      const { data, error } = await supabase.rpc("carrier_respond_to_counter", {
        p_negotiation_id: negotiationId,
        p_action: action,
        p_counter_amount: counterAmount ?? null,
        p_notes: null,
      });

      if (error) throw error;

      const result = data as { success: boolean; error?: string };
      if (!result.success) {
        const msg = result.error || "Action failed";
        if (
          msg.toLowerCase().includes("no longer active") ||
          msg.toLowerCase().includes("not found")
        ) {
          toast.message("This counter was already updated", {
            description: "Refreshing your offers…",
          });
          invalidateAll();
          return;
        }
        toast.error(msg);
        unlockOffer(offerId);
        return;
      }

      const labels = {
        accept: "Counter accepted! Broker will be notified.",
        reject: "Offer rejected.",
        counter: "Re-counter sent to broker!",
      };
      toast.success(labels[action]);

      invalidateAll();
      // Keep row locked — refetch will move it to the right history bucket.
    } catch (err: any) {
      toast.error(err.message || "Something went wrong");
      unlockOffer(offerId);
    } finally {
      setActionLoading(null);
    }
  };

  const counteredOffers = offers.filter(
    (o) => o.status === "countered" && !lockedOfferIds.has(o.id)
  );
  const activeOffers = offers.filter(
    (o) =>
      (o.status === "pending" || o.status === "accepted_by_carrier") &&
      !lockedOfferIds.has(o.id)
  );
  // Treat in-flight rows as "pending submission" so they don't render as
  // actionable counters even before the server flips status.
  const submittingOffers = offers.filter((o) => lockedOfferIds.has(o.id) && !(o.status === "accepted" || o.status === "rejected"));
  const allAwarded = offers.filter((o) => o.status === "accepted");
  const allDeclined = offers.filter((o) => o.status === "rejected");
  const awardedOffers = showCleared ? allAwarded : allAwarded.filter(o => !dismissed.has(o.id));
  const declinedOffers = showCleared ? allDeclined : allDeclined.filter(o => !dismissed.has(o.id));
  const clearedCount =
    (allAwarded.length - awardedOffers.length) +
    (allDeclined.length - declinedOffers.length);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (offers.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-muted-foreground gap-2">
        <Truck className="w-10 h-10 opacity-40" />
        <p className="text-sm">No offers yet. Book or negotiate loads from Search Loads.</p>
      </div>
    );
  }

  const renderOfferCard = (offer: OfferWithNegotiation) => {
    const route = offer.load
      ? `${offer.load.origin_city}, ${offer.load.origin_state} → ${offer.load.destination_city}, ${offer.load.destination_state}`
      : "Unknown route";

    return (
      <div
        key={offer.id}
        className={`rounded-lg border p-4 transition-colors ${
          offer.status === "countered"
            ? "border-accent bg-accent/5"
            : "bg-card"
        }`}
      >
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-sm font-semibold text-foreground truncate">
                {route}
              </span>
              <StatusBadge status={offer.status} />
            </div>
            <div className="flex items-center gap-3 text-xs text-muted-foreground">
              {offer.load && (
                <>
                  <span>#{offer.load.load_number}</span>
                  <span>{offer.load.equipment_type}</span>
                  {offer.load.total_miles && (
                    <span>{Math.round(offer.load.total_miles)} mi</span>
                  )}
                  <span>
                    Pickup:{" "}
                    {new Date(offer.load.pickup_date + "T00:00:00").toLocaleDateString(
                      "en-US",
                      { month: "short", day: "numeric" }
                    )}
                  </span>
                </>
              )}
            </div>
          </div>

          <div className="text-right shrink-0">
            <div className="flex items-center gap-1 text-sm font-bold text-foreground">
              <DollarSign className="w-3.5 h-3.5" />
              {Number(offer.rate_amount).toLocaleString()}
            </div>
            <span className="text-[10px] text-muted-foreground">Your offer</span>
          </div>
        </div>

        {/* Carrier's own note (always shown if present) */}
        {offer.notes && offer.status !== "countered" && (
          <div className="mt-2 text-xs bg-muted/40 rounded p-2 border border-border">
            <span className="text-muted-foreground font-semibold uppercase tracking-wide text-[10px]">Your note</span>
            <p className="mt-0.5 text-foreground whitespace-pre-wrap">{offer.notes}</p>
          </div>
        )}

        {/* Counter offer actions */}
        {offer.status === "countered" && offer.negotiation && (
          <div className="mt-3 pt-3 border-t border-border/50">
            <div className="flex items-center gap-2 mb-3">
              <ArrowLeftRight className="w-4 h-4 text-accent" />
              <span className="text-sm font-medium text-foreground">
                Broker countered:{" "}
                <span className="text-accent font-bold">
                  ${Number(offer.negotiation.counter_rate_amount).toLocaleString()}
                </span>
              </span>
              <span className="text-[10px] text-muted-foreground flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {new Date(offer.negotiation.created_at!).toLocaleString("en-US", {
                  month: "short",
                  day: "numeric",
                  hour: "numeric",
                  minute: "2-digit",
                })}
              </span>
            </div>

            {offer.negotiation.notes && (
              <div className="mb-3 text-xs bg-accent/5 rounded p-2 border border-accent/20">
                <span className="text-accent font-semibold uppercase tracking-wide text-[10px]">Broker note</span>
                <p className="mt-0.5 text-foreground whitespace-pre-wrap">{offer.negotiation.notes}</p>
              </div>
            )}
            <div className="flex items-center gap-2 flex-wrap">
              <Button
                size="sm"
                onClick={() => handleAction(offer.id, offer.negotiation!.id, "accept")}
                disabled={!!actionLoading || lockedOfferIds.has(offer.id)}
                className="bg-green-600 hover:bg-green-700 text-white"
              >
                {actionLoading === offer.negotiation.id + "accept" ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" />
                ) : (
                  <Check className="w-3.5 h-3.5 mr-1" />
                )}
                Accept ${Number(offer.negotiation.counter_rate_amount).toLocaleString()}
              </Button>

              <Button
                size="sm"
                variant="destructive"
                onClick={() => handleAction(offer.id, offer.negotiation!.id, "reject")}
                disabled={!!actionLoading || lockedOfferIds.has(offer.id)}
              >
                {actionLoading === offer.negotiation.id + "reject" ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" />
                ) : (
                  <X className="w-3.5 h-3.5 mr-1" />
                )}
                Reject
              </Button>

              {!showCounter[offer.id] ? (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() =>
                    setShowCounter((p) => ({ ...p, [offer.id]: true }))
                  }
                  disabled={!!actionLoading}
                >
                  <ArrowLeftRight className="w-3.5 h-3.5 mr-1" />
                  Re-Counter
                </Button>
              ) : (
                <div className="flex items-center gap-1.5">
                  <span className="text-xs text-muted-foreground">$</span>
                  <Input
                    type="number"
                    className="w-24 h-8 text-sm"
                    placeholder="Amount"
                    value={counterAmounts[offer.id] || ""}
                    onChange={(e) =>
                      setCounterAmounts((p) => ({
                        ...p,
                        [offer.id]: e.target.value,
                      }))
                    }
                  />
                  <Button
                    size="sm"
                    onClick={() => {
                      const amt = parseFloat(counterAmounts[offer.id]);
                      if (!amt || amt <= 0) {
                        toast.error("Enter a valid amount");
                        return;
                      }
                      handleAction(offer.id, offer.negotiation!.id, "counter", amt);
                    }}
                    disabled={!!actionLoading}
                  >
                    {actionLoading === offer.negotiation.id + "counter" ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      "Send"
                    )}
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      setShowCounter((p) => ({ ...p, [offer.id]: false }));
                      setCounterAmounts((p) => {
                        const { [offer.id]: _, ...rest } = p;
                        return rest;
                      });
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              )}
            </div>

            {lockedOfferIds.has(offer.id) && (
              <div className="mt-2 text-[11px] text-muted-foreground flex items-center gap-1">
                <Loader2 className="w-3 h-3 animate-spin" /> Submitting your response…
              </div>
            )}
          </div>
        )}

        {offer.status === "accepted" && (
          <div className="mt-3 pt-3 border-t border-emerald-200 flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2 text-emerald-800">
              <CheckCircle2 className="w-4 h-4" />
              <span className="text-sm font-semibold">Load awarded — it's yours</span>
            </div>
            <div className="flex items-center gap-3">
              <Link
                to="/my-loads"
                className="inline-flex items-center gap-1 text-sm font-semibold text-emerald-700 hover:text-emerald-900"
              >
                View in My Loads <ArrowRight className="w-3.5 h-3.5" />
              </Link>
              {dismissed.has(offer.id)
                ? <button type="button" onClick={() => handleRestore(offer.id)} className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1"><RotateCcw className="w-3 h-3" />Restore</button>
                : <button type="button" onClick={() => handleClear(offer.id)} className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1"><Trash2 className="w-3 h-3" />Clear</button>}
            </div>
          </div>
        )}

        {offer.status === "rejected" && (
          <div className="mt-3 pt-3 border-t border-border flex items-center justify-between gap-3">
            <span className="text-xs text-muted-foreground">This offer was declined. Browse other loads to find your next match.</span>
            {dismissed.has(offer.id)
              ? <button type="button" onClick={() => handleRestore(offer.id)} className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1 shrink-0"><RotateCcw className="w-3 h-3" />Restore</button>
              : <button type="button" onClick={() => handleClear(offer.id)} className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1 shrink-0"><Trash2 className="w-3 h-3" />Clear</button>}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="flex flex-col gap-4 p-4">
      {clearedCount > 0 && (
        <div className="flex justify-end">
          <button
            type="button"
            onClick={() => setShowCleared(s => !s)}
            className="text-xs text-muted-foreground hover:text-foreground"
          >
            {showCleared ? 'Hide cleared' : `Show cleared (${clearedCount})`}
          </button>
        </div>
      )}
      {counteredOffers.length > 0 && (
        <div>
          <h3 className="text-sm font-bold text-foreground mb-2 flex items-center gap-2">
            <ArrowLeftRight className="w-4 h-4 text-accent" />
            Action Required — Counter Offers ({counteredOffers.length})
          </h3>
          <div className="flex flex-col gap-2">
            {counteredOffers.map(renderOfferCard)}
          </div>
        </div>
      )}

      {(activeOffers.length > 0 || submittingOffers.length > 0) && (
        <div>
          <h3 className="text-sm font-bold text-foreground mb-2">
            Pending Offers ({activeOffers.length + submittingOffers.length})
          </h3>
          <div className="flex flex-col gap-2">
            {activeOffers.map(renderOfferCard)}
            {submittingOffers.map(renderOfferCard)}
          </div>
        </div>
      )}

      {awardedOffers.length > 0 && (
        <div>
          <h3 className="text-sm font-bold text-emerald-700 mb-2 flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4" />
            Awarded ({awardedOffers.length})
          </h3>
          <div className="flex flex-col gap-2">
            {awardedOffers.map(renderOfferCard)}
          </div>
        </div>
      )}

      {declinedOffers.length > 0 && (
        <div>
          <h3 className="text-sm font-bold text-muted-foreground mb-2">
            Declined ({declinedOffers.length})
          </h3>
          <div className="flex flex-col gap-2 opacity-70">
            {declinedOffers.map(renderOfferCard)}
          </div>
        </div>
      )}
    </div>
  );
}
